import {assertAndContinue} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as NavConstants from 'constants/NavConstants';
import {ProcessClientOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getNavigationURL} from 'nav/sidepanel/listing/SearchURLs';
import {getSelectedPageSize} from 'nav/sidepanel/PageSizeUtil';
import {FilterKey, keyToEmptyFilter, SearchParams} from 'search/SearchConstants';
import * as SearchPreferences from 'search/SearchPreferences';

function getModelFilteredByProcesses(
  modelType: ModelTypeConstants.PROCESS_TASK | ModelTypeConstants.PROCESS_OUTPUT,
  processIds: Array<string>,
  schemaId?: string
): SearchParams {
  assertAndContinue(processIds.length > 0, 'Expected at least one processId');
  return {
    filters: [
      {...keyToEmptyFilter(FilterKey.PROCESS_CLIENT_ORIGIN), value: ProcessClientOrigin.PROCESSES},
      {...keyToEmptyFilter(FilterKey.TYPES), value: [modelType]},
      ...(schemaId != null
        ? [
            {
              ...keyToEmptyFilter(FilterKey.SCHEMA),
              value: [schemaId],
            },
          ]
        : []),
      {...keyToEmptyFilter(FilterKey.PROCESS), value: processIds},
    ],
    limit: getSelectedPageSize(),
    offset: 0,
    query: '',
    sorts: SearchPreferences.getAppliedSortsFromUserPreference(NavConstants.Section.PROCESSES),
  };
}

function getOutputsFilteredByTasks(taskIds: Array<string>): SearchParams {
  assertAndContinue(taskIds.length > 0, 'Expected at least one taskId');
  return {
    filters: [
      {...keyToEmptyFilter(FilterKey.PROCESS_CLIENT_ORIGIN), value: ProcessClientOrigin.PROCESSES},
      {...keyToEmptyFilter(FilterKey.TYPES), value: [ModelTypeConstants.PROCESS_OUTPUT]},
      {...keyToEmptyFilter(FilterKey.PROCESS_TASK), value: taskIds},
    ],
    limit: getSelectedPageSize(),
    offset: 0,
    query: '',
    sorts: SearchPreferences.getAppliedSortsFromUserPreference(NavConstants.Section.PROCESSES),
  };
}

function getModelFilteredByHumanIds(
  modelType: ModelTypeConstants.PROCESS_TASK | ModelTypeConstants.PROCESS_OUTPUT,
  humanIds: Array<string>
): SearchParams {
  assertAndContinue(humanIds.length > 0, 'Expected at least one humanId');
  return {
    filters: [
      {...keyToEmptyFilter(FilterKey.PROCESS_CLIENT_ORIGIN), value: ProcessClientOrigin.PROCESSES},
      {...keyToEmptyFilter(FilterKey.TYPES), value: [modelType]},
      {...keyToEmptyFilter(FilterKey.NAMES), value: humanIds},
    ],
    limit: getSelectedPageSize(),
    offset: 0,
    query: '',
    sorts: SearchPreferences.getAppliedSortsFromUserPreference(NavConstants.Section.PROCESSES),
  };
}

function getFilterErrorsArray(searchParams: SearchParams): Array<null> {
  return new Array(searchParams.filters.length).fill(null);
}

function getDashboardUrl(searchParams: SearchParams): string {
  return getNavigationURL('/workflows', searchParams, getFilterErrorsArray(searchParams));
}

export function getTasksFilteredByHumanIds(taskHumanIds: Array<string>): string {
  const searchParams = getModelFilteredByHumanIds(ModelTypeConstants.PROCESS_TASK, taskHumanIds);
  return getDashboardUrl(searchParams);
}

export function getTasksFilteredByProcessesUrl(processIds: Array<string>, schemaId?: string): string {
  const searchParams = getModelFilteredByProcesses(ModelTypeConstants.PROCESS_TASK, processIds, schemaId);
  return getDashboardUrl(searchParams);
}

export function getOutputsFilteredByHumanIds(outputHumanIds: Array<string>): string {
  const searchParams = getModelFilteredByHumanIds(ModelTypeConstants.PROCESS_OUTPUT, outputHumanIds);
  return getDashboardUrl(searchParams);
}

export function getOutputsFilteredByProcessesUrl(processIds: Array<string>, schemaId?: string): string {
  const searchParams = getModelFilteredByProcesses(ModelTypeConstants.PROCESS_OUTPUT, processIds, schemaId);
  return getDashboardUrl(searchParams);
}

export function getOutputsFilteredByTasksUrl(taskIds: Array<string>): string {
  const searchParams = getOutputsFilteredByTasks(taskIds);
  return getDashboardUrl(searchParams);
}
